import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Pencil, Trash2, Plus } from "lucide-react";
import { ProgramPicker, SUBJECTS } from "@/components/calendar/EventForm";
import { PROGRAM_LABELS, fmtRange, fmtDate } from "@/lib/calendar";

const EMPTY = { title: "", subject: "none", programs: [], start_date: "", interval_weeks: 2, start: "19:00", end: "21:00", until: "", labels: {} };
const weekday = (ds) => (ds ? fmtDate(ds, { weekday: "long" }) : "");

function SeriesForm({ initial, onDone }) {
  const [f, setF] = useState({ ...EMPTY, ...initial, subject: initial.subject || "none", until: initial.until || "", labels: initial.labels || {} });
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const save = async () => {
    if (!f.title.trim() || !f.start_date || !f.programs.length || !f.start || !f.end) return toast.error("Fill in title, first class date, times and programs.");
    const body = { ...f, subject: f.subject === "none" ? null : f.subject, until: f.until || null, interval_weeks: Number(f.interval_weeks) };
    try {
      if (initial.id) await api.put(`/calendar/series/${initial.id}`, body); else await api.post("/calendar/series", body);
      toast.success("Class schedule saved."); onDone(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save schedule."); }
  };
  return (
    <div className="space-y-3 rounded-2xl border border-[#E6E1F5] p-4" data-testid="series-form">
      <div><Label>Class name</Label><Input data-testid="series-title-input" value={f.title} onChange={set("title")} className="mt-1.5 h-10 rounded-xl" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Subject</Label>
          <Select value={f.subject} onValueChange={set("subject")}><SelectTrigger className="mt-1.5 h-10 rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>{SUBJECTS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select></div>
        <div><Label>Repeats</Label>
          <Select value={String(f.interval_weeks)} onValueChange={set("interval_weeks")}><SelectTrigger data-testid="series-interval-select" className="mt-1.5 h-10 rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>{[1, 2, 3, 4].map((n) => <SelectItem key={n} value={String(n)}>{n === 1 ? "Every week" : `Every ${n} weeks`}</SelectItem>)}</SelectContent></Select></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>First class</Label><Input type="date" data-testid="series-start-date-input" value={f.start_date} onChange={set("start_date")} className="mt-1.5 h-10 rounded-xl" /></div>
        <div><Label>Ends (optional)</Label><Input type="date" value={f.until} onChange={set("until")} className="mt-1.5 h-10 rounded-xl" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Start</Label><Input type="time" data-testid="series-start-input" value={f.start} onChange={set("start")} className="mt-1.5 h-10 rounded-xl" /></div>
        <div><Label>End</Label><Input type="time" data-testid="series-end-input" value={f.end} onChange={set("end")} className="mt-1.5 h-10 rounded-xl" /></div>
      </div>
      <div><Label>Programs</Label><div className="mt-2"><ProgramPicker value={f.programs} onChange={set("programs")} prefix="series" /></div></div>
      {f.programs.map((p) => (
        <div key={p}><Label className="text-xs">Name shown to {PROGRAM_LABELS[p]} students (optional)</Label>
          <Input data-testid={`series-label-${p}`} value={f.labels[p] || ""} onChange={(e) => setF((s) => ({ ...s, labels: { ...s.labels, [p]: e.target.value } }))} placeholder={f.title} className="mt-1 h-9 rounded-xl" /></div>
      ))}
      <div className="flex gap-2">
        <Button onClick={save} data-testid="series-save-btn" className="rounded-xl ember-gradient font-bold">Save schedule</Button>
        <Button variant="ghost" onClick={() => onDone(false)} className="rounded-xl">Cancel</Button>
      </div>
    </div>
  );
}

export function SeriesManager({ open, onClose, onChanged }) {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/calendar/series").then((r) => setList(r.data));
  useEffect(() => { if (open) { load(); setEditing(null); } }, [open]);
  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.title}" and all its future classes?`)) return;
    await api.delete(`/calendar/series/${s.id}`); toast.success("Class schedule deleted."); load(); onChanged();
  };
  const done = (changed) => { setEditing(null); if (changed) { load(); onChanged(); } };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="mn max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="series-manager-dialog">
        <DialogHeader><DialogTitle className="text-xl font-extrabold">Regular class schedule</DialogTitle></DialogHeader>
        {editing ? <SeriesForm initial={editing} onDone={done} /> : (
          <div className="space-y-2">
            {list.map((s) => (
              <div key={s.id} className="flex items-center gap-3 rounded-xl border border-[#EEEAF8] p-3" data-testid={`series-row-${s.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-bold text-[#1A1530]">{s.title}</div>
                  <div className="text-xs text-[#6B6488]">{weekday(s.start_date)}s · {fmtRange(s)} · {s.interval_weeks === 1 ? "weekly" : `every ${s.interval_weeks} weeks`} from {fmtDate(s.start_date)} · {s.programs.map((p) => PROGRAM_LABELS[p]).join(", ")}</div>
                </div>
                <button onClick={() => setEditing(s)} data-testid={`series-edit-${s.id}`} className="rounded-lg p-2 text-[#4B2BB8] hover:bg-[#F3F0FF]"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => remove(s)} data-testid={`series-delete-${s.id}`} className="rounded-lg p-2 text-rose-600 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <Button onClick={() => setEditing({})} data-testid="series-add-btn" className="mt-2 rounded-xl ember-gradient font-bold"><Plus className="mr-1.5 h-4 w-4" /> Add regular class</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
